import {
  CheckCircleFilled,
  FileExclamationOutlined,
  FileProtectOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { Typography, type UploadFile } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SignatureModalButton } from './signature/SignatureModalButton.tsx';

const FileIcon = styled.span`
  flex-shrink: 0;
  font-size: 20px;
  line-height: 1;
`;

const FileInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  max-width: 100%;
`;

const FileCheckIcon = styled(CheckCircleFilled)`
  flex-shrink: 0;
  color: ${props => props.theme.colorSuccess};
`;

const FileName = styled(Typography.Text)`
  font-size: 16px;
  flex: 1;
  min-width: 0;
`;

export interface ISignerFileInfoRowProps {
  /** File appena selezionato dall'utente */
  file: RcFile | null;
  /** File firmato già caricato in precedenza */
  signedFile?: UploadFile | null;
  /** Dati di firma del documento (se disponibili) */
  signatures?: DettaglioFirma[];
}

/**
 * Riga informativa sul file selezionato/caricato nel flusso di firma.
 * Mostra icona, nome con ellipsis e check se il file è stato selezionato.
 * Se sono presenti dati di firma sul file firmato, mostra il pulsante per
 * aprire la modal di riepilogo.
 */
export function SignerFileInfoRow(props: Readonly<ISignerFileInfoRowProps>) {
  const { file, signedFile, signatures } = props;
  const { t } = useTranslation();

  const getFileInfo = () => {
    if (file) {
      return {
        icon: <PaperClipOutlined aria-hidden="true" />,
        name: file.name,
      };
    }
    if (signedFile) {
      return {
        icon: <FileProtectOutlined aria-hidden="true" />,
        name: signedFile.name,
      };
    }
    return {
      icon: <FileExclamationOutlined aria-hidden="true" />,
      name: t('signer.nessun-file-selezionato', 'Nessun file selezionato'),
    };
  };

  const fileInfo = getFileInfo();

  return (
    <FileInfoContainer>
      {file && <FileCheckIcon aria-hidden="true" />}
      <FileIcon>{fileInfo.icon}</FileIcon>
      <FileName ellipsis>{fileInfo.name}</FileName>
      {!file && (
        <SignatureModalButton signedFile={signedFile} signatures={signatures} />
      )}
    </FileInfoContainer>
  );
}
